import React, { useEffect, useState, useRef } from 'react';
import TextArea from 'components/shared/debouncedTextArea';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import request from 'utils/request_ajax';
import { useParams } from 'react-router-dom';
import { ORDER, COLUMNS, TYPES, routes as ROUTES } from 'constants/config';
import ScrollableContainer from 'components/shared/scrollable';
import { Icon, Spinner } from 'ui';
import LoadableState from 'components/shared/loadable';
import { isEmpty, uniqueId, findIndex, omit } from 'lodash/fp';
import { Drag } from 'components/ui/Icon/Streamline';
import { Image, BlankSlate, Description } from 'components/_shared/blank_slate';
import { pluralize } from 'utils/string_utils';
import * as actions from '../../../actions/journeyBuilder';
import { updateBanner, displayBanner } from '../../../actions/journeyBuilder';
import ContentPickerItem from './ContentPickerItem';
import TaskBuilder from './TaskBuilder';
import SelectEvent from './SelectEvent';
import { formatDate } from '../__helpers__/journeyHelper';

const contentTypes = {
  Lesson: [TYPES.LESSON, TYPES.RESOURCE],
  Path: [TYPES.PATH],
  Task: [TYPES.TASK],
  Event: [TYPES.EVENT],
};

const sortOptions = [
  {
    order: ORDER.DESC,
    type: COLUMNS.LAST_MODIFIED,
    label: 'Recently Updated',
  },
  { order: ORDER.ASC, type: COLUMNS.TITLE, label: 'Name A-Z' },
  { order: ORDER.DESC, type: COLUMNS.TITLE, label: 'Name Z-A' },
];

const PickerBlankSlate = ({ icon, description }) => (
  <BlankSlate large>
    <Image name={icon} />
    <Description>{description}</Description>
  </BlankSlate>
);

const ContentPicker = ({ step, idx, listeners, attributes }) => {
  const [viewLayout, setViewLayout] = useState('card');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(true);
  const [data, setData] = useState([]);
  const [query, setQuery] = useState('');
  const [error] = useState(null);
  const [sort, setSort] = useState({
    order: ORDER.DESC,
    type: COLUMNS.LAST_MODIFIED,
  });
  const [filter] = useState({
    type: contentTypes[step.contentType],
  });

  const dispatch = useDispatch();
  const { previewing } = useSelector(state => state.ui);
  const { journeyStatus } = useSelector(state => state);

  const { journeyId } = useParams();

  const dragContainer = useRef(null);

  const handleCancelClick = () => {
    if (step.editing) {
      dispatch(
        actions.updateStep({ index: idx, step: { ...step, editing: false } }),
      );
    } else {
      dispatch(actions.removeStep({ index: idx }));
    }
  };

  const handleQueryChange = value => {
    setQuery(value);
  };

  const handleLayoutChange = e => {
    setViewLayout(e.target.value);
  };

  const handleSortChange = e => {
    setSort(omit('label', sortOptions[e.target.value]));
  };

  const getContentsRoute = () => {
    if (step.contentType === 'certification') {
      return ROUTES.PATH_ADD_CONTENT_CERTIFICATION;
    }

    return ROUTES.PATH_ADD_CONTENT;
  };

  const fetchData = fetchPage => {
    setLoading(true);

    request
      .get(getContentsRoute(), { fetchPage, query, sort, filter })
      .then(({ data: resData, ...res }) => {
        setLoading(false);
        setSuccess(true);
        setData(fetchPage ? data.concat(resData) : resData);
      })
      .fail(() => {
        setLoading(false);
        setSuccess(false);
      });
  };

  const selectContent = item => {
    const dispatchContent = {
      index: idx,
      step: {
        contentId: item.id,
        contentType: step.contentType,
        description: step.description,
        id: step.id,
        stepId: step.stepId,
        title: item.title,
        editing: false,
        targetStepId: step.targetStepId,
      },
    };

    if (dispatchContent.step.targetStepId === null) {
      delete dispatchContent.step.targetStepId;
    }
    dispatch(actions.toggleFinishingEditing(true));
    dispatch(actions.updateStep(dispatchContent));

    if (dispatchContent.step.stepId) {
      request
        .patchJSON(
          ROUTES.JOURNEY.STEPS.RESOURCE(journeyId, dispatchContent.step.stepId),
          {
            content_id: dispatchContent.step.contentId,
            content_type: dispatchContent.step.contentType,
            journey_status: journeyStatus,
          },
        )
        .then(responseData => {
          dispatchContent.step.description = responseData.description;
          // Load path contents
          if (item.type === TYPES.PATH) {
            dispatchContent.step.contents = responseData.contents;
          }
          dispatch(actions.updateStep(dispatchContent));
          dispatch(actions.toggleFinishingEditing(false));
          dispatch(
            actions.updateAutosavedAt({
              autosavedAt: formatDate(responseData.updatedAt),
            }),
          );
          dispatch(
            actions.updateFields({
              lastUpdater: responseData.lastUpdater,
              updatedAt: responseData.updatedAt,
            }),
          );
          dispatch(
            actions.updateJourneyStatus({
              journeyStatus: responseData.journeyStatus,
            }),
          );
        })
        .fail(err => {
          console.error(err);
          dispatch(
            updateBanner({
              message: t('journeys.builder.reorder_error'),
              status: 'error',
            }),
          );
          dispatch(
            displayBanner({
              bannerDismissible: false,
            }),
          );
        });
    } else {
      request
        .postJSON(ROUTES.JOURNEY.STEPS.CREATE(journeyId), {
          content_type: dispatchContent.step.contentType,
          content_id: dispatchContent.step.contentId,
          target_step_id: dispatchContent.step.targetStepId,
          journey_status: journeyStatus,
        })
        .then(responseData => {
          dispatchContent.step.stepId = responseData.stepId;
          dispatchContent.step.id = responseData.stepId;
          dispatchContent.step.description = responseData.description;
          // Load path contents
          if (item.type === TYPES.PATH) {
            dispatchContent.step.contents = responseData.contents;
          }
          dispatch(actions.updateStep(dispatchContent));
          dispatch(actions.toggleFinishingEditing(false));
          dispatch(
            actions.updateAutosavedAt({
              autosavedAt: formatDate(responseData.updatedAt),
            }),
          );
          dispatch(
            actions.updateFields({
              lastUpdater: responseData.lastUpdater,
              updatedAt: responseData.updatedAt,
            }),
          );
          dispatch(
            actions.updateJourneyStatus({
              journeyStatus: responseData.journeyStatus,
            }),
          );
        })
        .fail(err => {
          console.error(err);
          dispatch(
            updateBanner({
              message: t('journeys.builder.reorder_error'),
              status: 'error',
            }),
          );
          dispatch(
            displayBanner({
              bannerDismissible: false,
            }),
          );
        });
    }
  };

  const renderContent = () => {
    return data.map(item => {
      const isPath = item.type === TYPES.PATH;
      const iconName = isPath && 'path';

      return (
        <ContentPickerItem
          {...item}
          key={item.id}
          tags={item.meta.tags}
          layout={viewLayout}
          iconName={iconName}
          onClick={() => selectContent(item)}
          count={item.meta.stepsCount}
          countLabel={isPath && pluralize(item.meta.stepsCount, 'Lesson')}
        />
      );
    });
  };

  const getInternationalizationKey = contentType => {
    switch (contentType) {
      case 'Lesson':
        return 'lesson';
      case 'Path':
        return 'path';
      case 'Certification':
        return 'certification';
      case 'Task':
        return 'task';
      case 'Event':
        return 'event';
      default:
        return 'UnknownType';
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort]);

  const loadedFailure = !loading && !success;
  const loadedSuccess = !loading && success;

  const isCardLayout = viewLayout === 'card';
  const isListLayout = viewLayout === 'list';

  const dataViewClassName = classNames('JourneyContentPicker-dataView', {
    'JourneyContentPicker-dataView--card': isCardLayout,
    'JourneyContentPicker-dataView--list': isListLayout,
  });

  const layoutButtonClassName = 'u-resetBtn JourneyContentPicker-layoutBtn';
  const cardLayoutButtonClass = classNames(layoutButtonClassName, {
    'is-active': isCardLayout,
  });
  const listLayoutButtonClass = classNames(layoutButtonClassName, {
    'is-active': isListLayout,
  });

  const journeyContentTitle = classNames('JourneyContent-title', {
    'JourneyContent-titleBuilder': !previewing,
  });

  if (step.contentType === 'Task') {
    return (
      <TaskBuilder
        step={step}
        idx={idx}
        listeners={listeners}
        attributes={attributes}
        handleCancelClick={handleCancelClick}
      />
    );
  }

  if (step.contentType === 'Event') {
    return (
      <SelectEvent
        step={step}
        idx={idx}
        listeners={listeners}
        attributes={attributes}
        handleCancelClick={handleCancelClick}
      />
    );
  }

  return (
    <div className="JourneyContent JourneyContentPicker">
      <div ref={dragContainer}>
        <div className="JourneyContent-header">
          <div className="JourneyContentPicker-titleContainer">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <div className={journeyContentTitle}>
              {t('journeys.builder.select', {
                type: t(
                  `journeys.element_types.${getInternationalizationKey(
                    step.contentType,
                  )}`,
                ),
              })}
            </div>
          </div>
          <button
            type="button"
            className="u-linkBtn u-linkBtn--dark td-u"
            onClick={handleCancelClick}
            children="Cancel"
          />
        </div>
        <div className="JourneyContentPicker-body">
          <TextArea
            onChange={handleQueryChange}
            value={query}
            className="AssignModal-searchInput"
            placeholder="Search content"
            singleLine
            maxLength="255"
          />
          <div className="d-f jc-sb ai-c mb-12">
            <div className="JourneyContentPicker-layoutOptions">
              <button
                className={cardLayoutButtonClass}
                onClick={handleLayoutChange}
                value="card"
                type="button"
              >
                <Icon name="thumb" stabilize={false} />
              </button>
              <button
                className={listLayoutButtonClass}
                onClick={handleLayoutChange}
                value="list"
                type="button"
              >
                <Icon name="thumb-list" stabilize={false} />
              </button>
            </div>
            <div className="AssignModal-sortButton">
              <select
                className="NewButton NewButton--white"
                onChange={handleSortChange}
                value={findIndex(sort, sortOptions)}
              >
                {sortOptions.map((option, i) => (
                  <option
                    key={uniqueId('option')}
                    value={i}
                    children={option.label}
                  />
                ))}
              </select>
              <div className="AssignModal-sortIcon">
                <Icon name="sort" />
              </div>
            </div>
          </div>
          {error && (
            <div className="CommentForm-errorMessage mb-12">{error}</div>
          )}
          {loading && isEmpty(data) && (
            <ScrollableContainer className="d-f jc-c ai-c" maxHeight={null}>
              <div>
                <Spinner />
                <span className="ml-8 c-brand-gray-darker">Loading...</span>
              </div>
            </ScrollableContainer>
          )}
          {loadedFailure && (
            <PickerBlankSlate
              icon="danger"
              description="Something went wrong. Please try again."
            />
          )}
          {loadedSuccess && isEmpty(data) && (
            <PickerBlankSlate
              icon="filter"
              description="We couldn't find any Learning Content"
            />
          )}
          {!isEmpty(data) && (
            <ScrollableContainer maxHeight={null}>
              <LoadableState loading={loading}>
                <div className={dataViewClassName} children={renderContent()} />
              </LoadableState>
            </ScrollableContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPicker;
